import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { message } from "antd";
import useSupportStore from "../../stores/useSupportStore";
import useAuthStore from "../../stores/useAuthStore";

const TYPES = {
  bug: { label: "Bug", color: "#ef4444", fill: "rgba(239,68,68,0.15)", lightBg: "#fef2f2" },
  improvement: { label: "Improvement", color: "#3b82f6", fill: "rgba(59,130,246,0.15)", lightBg: "#eff6ff" },
  question: { label: "Question", color: "#f59e0b", fill: "rgba(245,158,11,0.15)", lightBg: "#fffbeb" },
  praise: { label: "Praise", color: "#22c55e", fill: "rgba(34,197,94,0.15)", lightBg: "#f0fdf4" },
};

function normalizeRect(a, b) {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
  };
}

// ─── Annotation Panel ───────────────────────────────────────────────────────

function AnnotationPanel({ rect, onSave, onCancel }) {
  const [comment, setComment] = useState("");
  const [type, setType] = useState("bug");
  const textRef = useRef(null);

  useEffect(() => { textRef.current?.focus(); }, []);

  const vpW = window.innerWidth;
  const vpH = window.innerHeight;
  const panelW = 280;
  const gap = 12;
  const left = rect.x + rect.width + gap + panelW > vpW
    ? Math.max(8, rect.x - panelW - gap)
    : rect.x + rect.width + gap;
  const top = Math.max(60, Math.min(rect.y, vpH - 320));

  return (
    <div
      style={{
        position: "fixed", zIndex: 200010, width: panelW, left, top,
        borderRadius: 12, border: "1px solid #e5e7eb", background: "#fff",
        boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e5e7eb", padding: "10px 14px" }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Add note</span>
        <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#999", padding: "0 4px", lineHeight: 1 }}>&times;</button>
      </div>

      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>Type</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {Object.entries(TYPES).map(([key, cfg]) => (
              <button
                key={key}
                onClick={() => setType(key)}
                style={{
                  padding: "6px 8px", borderRadius: 6, fontSize: 12, fontWeight: 500,
                  cursor: "pointer", transition: "all 0.15s",
                  border: type === key ? `2px solid ${cfg.color}` : "1px solid #e5e7eb",
                  background: type === key ? cfg.lightBg : "#fff",
                  color: type === key ? cfg.color : "#666",
                }}
              >
                {cfg.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Comment *</div>
          <textarea
            ref={textRef}
            placeholder="Describe the issue or suggestion..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            style={{
              width: "100%", padding: "8px 10px", border: "1px solid #d9d9d9",
              borderRadius: 6, fontSize: 13, resize: "none", outline: "none",
              boxSizing: "border-box", fontFamily: "inherit",
            }}
            onFocus={(e) => { e.target.style.borderColor = "#1890ff"; }}
            onBlur={(e) => { e.target.style.borderColor = "#d9d9d9"; }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && comment.trim()) {
                onSave(comment, type);
              }
              if (e.key === "Escape") onCancel();
            }}
          />
          <div style={{ fontSize: 10, color: "#bbb", marginTop: 2 }}>Ctrl+Enter to save</div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #e5e7eb", padding: "10px 14px" }}>
        <button
          onClick={onCancel}
          style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #d9d9d9", background: "#fff", fontSize: 12, cursor: "pointer" }}
        >
          Discard
        </button>
        <button
          onClick={() => comment.trim() && onSave(comment, type)}
          disabled={!comment.trim()}
          style={{
            padding: "5px 12px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 500,
            background: comment.trim() ? "#1890ff" : "#d9d9d9",
            color: "#fff", cursor: comment.trim() ? "pointer" : "not-allowed",
          }}
        >
          Save note
        </button>
      </div>
    </div>
  );
}

// ─── Annotation Pin ─────────────────────────────────────────────────────────

function AnnotationPin({ annotation, index, onRemove }) {
  const [hovered, setHovered] = useState(false);
  const cfg = TYPES[annotation.type];

  return (
    <div style={{ position: "absolute", left: annotation.rect.x, top: annotation.rect.y, pointerEvents: "auto" }}>
      <div style={{
        position: "absolute", width: annotation.rect.width, height: annotation.rect.height,
        border: `2px solid ${cfg.color}`, background: cfg.fill, borderRadius: 4, pointerEvents: "none",
      }} />
      <button
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: "absolute", top: -14, left: -14, zIndex: 10,
          width: 26, height: 26, borderRadius: "50%", border: "2px solid #fff",
          background: cfg.color, color: "#fff", fontSize: 11, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          transition: "transform 0.15s", transform: hovered ? "scale(1.15)" : "scale(1)",
        }}
      >
        {index + 1}
      </button>
      {hovered && (
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            position: "absolute", left: 16, top: -8, zIndex: 20, width: 220,
            borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: 10, pointerEvents: "auto",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{
              padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 600,
              background: cfg.lightBg, color: cfg.color, border: `1px solid ${cfg.color}30`,
            }}>
              {cfg.label}
            </span>
            <button
              onClick={() => onRemove(annotation.id)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#999", fontSize: 16, padding: "0 2px", lineHeight: 1 }}
            >
              &times;
            </button>
          </div>
          <p style={{ fontSize: 12, color: "#333", lineHeight: 1.4, margin: 0 }}>{annotation.comment}</p>
        </div>
      )}
    </div>
  );
}

// ─── Review Panel ───────────────────────────────────────────────────────────

function ReviewPanel({
  annotations, screenshotUrl, capturingScreenshot, submitting, recordingData,
  onRemove, onAddMore, onSubmit, onClose,
}) {
  return (
    <div
      style={{
        position: "fixed", top: 0, right: 0, zIndex: 200010, height: "100vh", width: 360,
        display: "flex", flexDirection: "column", borderLeft: "1px solid #e5e7eb",
        background: "#fff", boxShadow: "-8px 0 30px rgba(0,0,0,0.15)",
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e5e7eb", padding: "14px 18px", flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Review & Submit</div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            {annotations.length} annotation{annotations.length !== 1 ? "s" : ""} ready
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#999", padding: "0 4px", lineHeight: 1 }}>&times;</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px" }}>
        {/* Screenshot */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>Screenshot</div>
          <div style={{
            border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden",
            background: "#f9fafb", minHeight: 100, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {capturingScreenshot ? (
              <div style={{ padding: 24, textAlign: "center", color: "#999" }}>
                <div className="annotator-spinner" style={{ margin: "0 auto 8px" }} />
                <div style={{ fontSize: 12 }}>Capturing screen...</div>
              </div>
            ) : screenshotUrl ? (
              <img src={screenshotUrl} alt="Screenshot with annotations" style={{ width: "100%", display: "block" }} />
            ) : (
              <div style={{ padding: 24, textAlign: "center", color: "#999", fontSize: 12 }}>Screenshot unavailable</div>
            )}
          </div>
        </div>

        <div style={{ height: 1, background: "#e5e7eb", margin: "12px 0" }} />

        {/* Annotations list */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>Annotations</div>
          {annotations.length === 0 ? (
            <div style={{ fontSize: 12, color: "#999", fontStyle: "italic" }}>No annotations yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {annotations.map((ann, idx) => {
                const cfg = TYPES[ann.type];
                return (
                  <div key={ann.id} style={{
                    display: "flex", alignItems: "flex-start", gap: 8,
                    border: "1px solid #e5e7eb", borderRadius: 8, padding: 10,
                  }}>
                    <span style={{
                      marginTop: 2, flexShrink: 0, width: 20, height: 20, borderRadius: "50%",
                      background: cfg.color, color: "#fff", fontSize: 10, fontWeight: 700,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {idx + 1}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        display: "inline-block", padding: "1px 6px", borderRadius: 4,
                        fontSize: 10, fontWeight: 600, background: cfg.lightBg, color: cfg.color,
                        marginBottom: 4,
                      }}>
                        {cfg.label}
                      </span>
                      <p style={{ fontSize: 12, color: "#333", margin: 0, lineHeight: 1.4 }}>{ann.comment}</p>
                    </div>
                    <button
                      onClick={() => onRemove(ann.id)}
                      style={{ flexShrink: 0, background: "none", border: "none", cursor: "pointer", color: "#ccc", fontSize: 16, padding: "2px", lineHeight: 1 }}
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <button
            onClick={onAddMore}
            style={{
              width: "100%", padding: "8px 12px", border: "1px dashed #d9d9d9",
              borderRadius: 6, background: "#fff", fontSize: 12, color: "#666",
              cursor: "pointer", marginTop: 8,
            }}
          >
            + Add more annotations
          </button>
        </div>

        {/* Recording data badge */}
        {recordingData && (
          <div style={{
            border: "1px solid #fecaca", borderRadius: 8, padding: 12,
            background: "#fef2f2", marginBottom: 16,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%", background: "#ef4444",
              }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: "#991b1b" }}>
                Recording attached ({recordingData.durationFormatted})
              </span>
            </div>
            <div style={{ fontSize: 11, color: "#7f1d1d", lineHeight: 1.6 }}>
              {recordingData.counts.events} trace events, {recordingData.counts.errors} errors, {recordingData.counts.consoleErrors} console errors
            </div>
          </div>
        )}
      </div>

      {/* Submit footer */}
      <div style={{ borderTop: "1px solid #e5e7eb", padding: "14px 18px", flexShrink: 0 }}>
        <button
          onClick={onSubmit}
          disabled={annotations.length === 0 || submitting}
          style={{
            width: "100%", padding: "10px 16px", border: "none", borderRadius: 8,
            background: (annotations.length === 0 || submitting) ? "#d9d9d9" : "#1890ff",
            color: "#fff", fontSize: 14, fontWeight: 600,
            cursor: (annotations.length === 0 || submitting) ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          {submitting ? "Creating ticket..." : `Submit feedback (${annotations.length})`}
        </button>
        <div style={{ fontSize: 10, color: "#999", textAlign: "center", marginTop: 6 }}>
          Creates a support ticket with your screenshot and annotations
        </div>
      </div>
    </div>
  );
}

// ─── Draw annotations on canvas ────────────────────────────────────────────

function drawAnnotationsOnCanvas(canvas, annotations) {
  const ctx = canvas.getContext("2d");
  if (!ctx || annotations.length === 0) return;

  const vpW = window.innerWidth;
  const vpH = window.innerHeight;
  const scaleX = canvas.width / vpW;
  const scaleY = canvas.height / vpH;
  const SCALE = scaleX;

  const COLORS = {
    bug: { border: "#ef4444", fill: "rgba(239,68,68,0.18)", pin: "#ef4444" },
    improvement: { border: "#3b82f6", fill: "rgba(59,130,246,0.18)", pin: "#3b82f6" },
    question: { border: "#f59e0b", fill: "rgba(245,158,11,0.18)", pin: "#f59e0b" },
    praise: { border: "#22c55e", fill: "rgba(34,197,94,0.18)", pin: "#22c55e" },
  };

  annotations.forEach((ann, idx) => {
    const col = COLORS[ann.type] || COLORS.bug;
    const cx = ann.rect.x * scaleX;
    const cy = ann.rect.y * scaleY;
    const cw = ann.rect.width * scaleX;
    const ch = ann.rect.height * scaleY;

    ctx.fillStyle = col.fill;
    ctx.fillRect(cx, cy, cw, ch);

    ctx.strokeStyle = col.border;
    ctx.lineWidth = 2.5 * SCALE;
    ctx.strokeRect(cx + SCALE, cy + SCALE, cw - 2 * SCALE, ch - 2 * SCALE);

    // Pin circle
    const pinR = 12 * SCALE;
    const pinX = cx + pinR + 2 * SCALE;
    const pinY = cy + pinR + 2 * SCALE;
    ctx.beginPath();
    ctx.arc(pinX, pinY, pinR, 0, Math.PI * 2);
    ctx.fillStyle = col.pin;
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2 * SCALE;
    ctx.stroke();

    ctx.font = `bold ${Math.round(pinR * 1.1)}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#fff";
    ctx.fillText(String(idx + 1), pinX, pinY + 0.5 * SCALE);
  });

  // Legend
  const padX = 14 * SCALE;
  const padY = 14 * SCALE;
  const lineH = 18 * SCALE;
  const legendW = 260 * SCALE;
  const legendH = (annotations.length * lineH) + 22 * SCALE;
  const legendX = padX;
  const legendY = canvas.height - legendH - padY;

  ctx.fillStyle = "rgba(0,0,0,0.55)";
  const r = 8 * SCALE;
  ctx.beginPath();
  ctx.moveTo(legendX + r, legendY);
  ctx.lineTo(legendX + legendW - r, legendY);
  ctx.arcTo(legendX + legendW, legendY, legendX + legendW, legendY + r, r);
  ctx.lineTo(legendX + legendW, legendY + legendH - r);
  ctx.arcTo(legendX + legendW, legendY + legendH, legendX + legendW - r, legendY + legendH, r);
  ctx.lineTo(legendX + r, legendY + legendH);
  ctx.arcTo(legendX, legendY + legendH, legendX, legendY + legendH - r, r);
  ctx.lineTo(legendX, legendY + r);
  ctx.arcTo(legendX, legendY, legendX + r, legendY, r);
  ctx.closePath();
  ctx.fill();

  ctx.font = `bold ${Math.round(11 * SCALE)}px sans-serif`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillStyle = "#e5e7eb";
  ctx.fillText("Feedback Annotations", legendX + 10 * SCALE, legendY + 8 * SCALE);

  annotations.forEach((ann, idx) => {
    const col = COLORS[ann.type] || COLORS.bug;
    const itemY = legendY + 20 * SCALE + idx * lineH;
    const bR = 7 * SCALE;

    ctx.beginPath();
    ctx.arc(legendX + 14 * SCALE, itemY + lineH * 0.5, bR, 0, Math.PI * 2);
    ctx.fillStyle = col.pin;
    ctx.fill();

    ctx.font = `bold ${Math.round(7 * SCALE)}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#fff";
    ctx.fillText(String(idx + 1), legendX + 14 * SCALE, itemY + lineH * 0.5 + 0.5 * SCALE);

    ctx.font = `${Math.round(9 * SCALE)}px sans-serif`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#d1d5db";
    const maxChars = Math.floor(legendW / (6 * SCALE));
    const label = ann.comment.length > maxChars ? ann.comment.slice(0, maxChars - 1) + "\u2026" : ann.comment;
    ctx.fillText(label, legendX + 26 * SCALE, itemY + lineH * 0.5 + 0.5 * SCALE);
  });
}

// ─── Main FeedbackOverlay ───────────────────────────────────────────────────

export default function FeedbackOverlay({ open, onClose }) {
  const [mode, setMode] = useState("selecting");
  const [dragStart, setDragStart] = useState(null);
  const [liveRect, setLiveRect] = useState(null);
  const [pendingRect, setPendingRect] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isDragging = useRef(false);

  // Reset on open/close
  useEffect(() => {
    if (!open) return;
    setMode("selecting");
    setLiveRect(null);
    setPendingRect(null);
    setDragStart(null);
    setAnnotations([]);
    setScreenshotUrl(null);
    setSubmitting(false);
    isDragging.current = false;
  }, [open]);

  // ESC key handler
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") {
        if (mode === "annotating") {
          setPendingRect(null);
          setMode("selecting");
        } else if (mode === "reviewing") {
          setMode("selecting");
          setScreenshotUrl(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, mode, onClose]);

  // ── Drag handlers ──

  const handleMouseDown = useCallback((e) => {
    if (mode !== "selecting") return;
    e.preventDefault();
    isDragging.current = true;
    setDragStart({ x: e.clientX, y: e.clientY });
    setLiveRect(null);
  }, [mode]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current || !dragStart) return;
    setLiveRect(normalizeRect(dragStart, { x: e.clientX, y: e.clientY }));
  }, [dragStart]);

  const handleMouseUp = useCallback((e) => {
    if (!isDragging.current || !dragStart) return;
    isDragging.current = false;
    const rect = normalizeRect(dragStart, { x: e.clientX, y: e.clientY });
    if (rect.width > 10 && rect.height > 10) {
      setPendingRect(rect);
      setLiveRect(null);
      setMode("annotating");
    } else {
      setLiveRect(null);
      setDragStart(null);
    }
  }, [dragStart]);

  // ── Annotation management ──

  const handleSaveAnnotation = useCallback((comment, type) => {
    if (!pendingRect) return;
    setAnnotations((prev) => [...prev, {
      id: `ann-${Date.now()}`,
      rect: pendingRect,
      comment,
      type,
      submittedAt: new Date().toISOString(),
    }]);
    setPendingRect(null);
    setMode("selecting");
  }, [pendingRect]);

  const handleDiscardPending = useCallback(() => {
    setPendingRect(null);
    setMode("selecting");
  }, []);

  const handleRemoveAnnotation = useCallback((id) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // ── Review — capture screenshot ──

  const handleReview = useCallback(async () => {
    setMode("reviewing");
    setCapturingScreenshot(true);
    try {
      // Hide overlay to capture clean screenshot
      const overlayEl = document.getElementById("feedback-overlay-root");
      if (overlayEl) overlayEl.style.visibility = "hidden";
      await new Promise((r) => setTimeout(r, 150));

      const base64 = await window.electronAPI.support.captureScreenshot();

      if (overlayEl) overlayEl.style.visibility = "";

      // Draw annotations onto captured image
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        drawAnnotationsOnCanvas(canvas, annotations);
        setScreenshotUrl(canvas.toDataURL("image/jpeg", 0.75));
        setCapturingScreenshot(false);
      };
      img.onerror = () => setCapturingScreenshot(false);
      img.src = `data:image/jpeg;base64,${base64}`;
    } catch (err) {
      console.error("[Feedback] Screenshot capture failed:", err);
      setCapturingScreenshot(false);
      const overlayEl = document.getElementById("feedback-overlay-root");
      if (overlayEl) overlayEl.style.visibility = "";
    }
  }, [annotations]);

  // ── Submit ──

  const handleSubmit = useCallback(async () => {
    if (annotations.length === 0) return;
    setSubmitting(true);

    const authState = useAuthStore.getState();
    const username = authState.username || authState.tillData?.tillAccess?.csUser?.name || "POS User";

    const recordingData = useSupportStore.getState().recordingData;
    const hasRecording = !!recordingData;

    const subject = annotations.length === 1
      ? `[${TYPES[annotations[0].type]?.label || annotations[0].type}] ${annotations[0].comment.slice(0, 80)}`
      : `Visual feedback \u2014 ${annotations.length} annotations`;

    const description = [
      ...annotations.map((a, i) => `${i + 1}. [${TYPES[a.type]?.label || a.type}] ${a.comment}`),
      ...(hasRecording ? [`\n--- Recording Session (${recordingData.durationFormatted}) ---`, `Events: ${recordingData.counts.events}, Errors: ${recordingData.counts.errors}, Console Errors: ${recordingData.counts.consoleErrors}`] : []),
    ].join("\n");

    const items = annotations.map((a) => ({
      category: a.type,
      description: a.comment,
    }));

    const screenshotData = screenshotUrl?.replace(/^data:image\/jpeg;base64,/, "") || null;

    const result = await useSupportStore.getState().submitTicket({
      category: hasRecording ? "recorded_issue" : "feedback",
      priority: annotations.some((a) => a.type === "bug") ? "high" : "medium",
      subject,
      description,
      items,
      submittedBy: username,
      screenshotData,
      recordingData: recordingData || undefined,
    });

    setSubmitting(false);

    if (result.success) {
      useSupportStore.getState().clearRecordingData();
      message.success(hasRecording ? "Recorded issue submitted! Ticket created." : "Feedback submitted! Ticket created.");
      onClose();
    } else {
      message.error(result.error || "Failed to submit feedback");
    }
  }, [annotations, screenshotUrl, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      id="feedback-overlay-root"
      style={{
        position: "fixed", inset: 0, zIndex: 200000,
        userSelect: "none",
        cursor: mode === "selecting" ? "crosshair" : "default",
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Dim layer */}
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.2)", pointerEvents: "none" }} />

      {/* ── Top toolbar ── */}
      <div
        style={{
          position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
          zIndex: 200001, display: "flex", alignItems: "center", gap: 8,
          borderRadius: 12, border: "1px solid #e5e7eb", background: "#fff",
          boxShadow: "0 8px 30px rgba(0,0,0,0.15)", padding: "8px 14px",
          pointerEvents: "auto", whiteSpace: "nowrap",
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 10, borderRight: "1px solid #e5e7eb" }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: "#333" }}>
            {mode === "selecting" && "Drag to highlight an area"}
            {mode === "annotating" && "Add a note for the selected area"}
            {mode === "reviewing" && "Review your annotations"}
          </span>
        </div>

        {annotations.length > 0 && (
          <span style={{ padding: "2px 8px", borderRadius: 10, background: "#f0f0f0", fontSize: 11, fontWeight: 600, color: "#666" }}>
            {annotations.length} note{annotations.length !== 1 ? "s" : ""}
          </span>
        )}

        {annotations.length > 0 && mode === "selecting" && (
          <button
            onClick={() => setAnnotations((prev) => prev.slice(0, -1))}
            className="fb-toolbar-btn"
          >
            Undo
          </button>
        )}

        <div style={{ paddingLeft: 8, borderLeft: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 6 }}>
          {mode === "selecting" && (
            <button
              disabled={annotations.length === 0}
              onClick={handleReview}
              style={{
                padding: "5px 12px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 500,
                background: annotations.length > 0 ? "#1890ff" : "#d9d9d9",
                color: "#fff", cursor: annotations.length > 0 ? "pointer" : "not-allowed",
              }}
            >
              Review & Send
            </button>
          )}
          {mode === "reviewing" && (
            <button
              onClick={() => { setMode("selecting"); setScreenshotUrl(null); }}
              className="fb-toolbar-btn"
            >
              + Add more
            </button>
          )}
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#999", padding: "0 6px", lineHeight: 1 }}
          >
            &times;
          </button>
        </div>
      </div>

      {/* ── Live drag rectangle ── */}
      {liveRect && liveRect.width > 0 && liveRect.height > 0 && (
        <div style={{
          position: "absolute", left: liveRect.x, top: liveRect.y,
          width: liveRect.width, height: liveRect.height,
          border: "2px solid #1890ff", background: "rgba(24,144,255,0.1)",
          borderRadius: 4, pointerEvents: "none",
        }} />
      )}

      {/* ── Pending rect (pulsing) ── */}
      {pendingRect && (
        <div
          className="fb-pending-rect"
          style={{
            position: "absolute", left: pendingRect.x, top: pendingRect.y,
            width: pendingRect.width, height: pendingRect.height,
          }}
        />
      )}

      {/* ── Saved annotation pins ── */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {annotations.map((ann, idx) => (
          <AnnotationPin key={ann.id} annotation={ann} index={idx} onRemove={handleRemoveAnnotation} />
        ))}
      </div>

      {/* ── Annotation panel ── */}
      {pendingRect && mode === "annotating" && (
        <AnnotationPanel rect={pendingRect} onSave={handleSaveAnnotation} onCancel={handleDiscardPending} />
      )}

      {/* ── Review drawer ── */}
      {mode === "reviewing" && (
        <ReviewPanel
          annotations={annotations}
          screenshotUrl={screenshotUrl}
          capturingScreenshot={capturingScreenshot}
          submitting={submitting}
          recordingData={useSupportStore.getState().recordingData}
          onRemove={handleRemoveAnnotation}
          onAddMore={() => { setMode("selecting"); setScreenshotUrl(null); }}
          onSubmit={handleSubmit}
          onClose={onClose}
        />
      )}
    </div>,
    document.body
  );
}
